import React, { useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { LoadingSpinner, ToastNotification } from '../../components/atoms'
import { useToast } from '../../hooks'
import usePendingReviewData from '../../hooks/usePendingReviewData'
import { authenticatedFetchJson } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import logger from '../../utils/logger'
import styles from './AdminBillingPendingReview.module.scss'
import OpsBar from './pendingReviewOps/OpsBar'

const formatCurrency = (minorAmount, currency = 'CAD') => {
  const amount = (minorAmount || 0) / 100
  return new window.Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
}

const formatPeriod = (start, end) => {
  if (!start) return '\u2014'
  const s = new Date(start)
  const e = end ? new Date(end) : s
  const sameMonth = s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()
  if (sameMonth) {
    return s.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
  }
  const fmt = { year: 'numeric', month: 'short' }
  return `${s.toLocaleDateString('en-US', fmt)} \u2013 ${e.toLocaleDateString('en-US', fmt)}`
}

const EmptyQueue = () => (
  <div className={styles.emptyState}>
    <div className={styles.emptyIcon}>
      <FontAwesomeIcon icon="clipboard-check" />
    </div>
    <h3 className={styles.emptyTitle}>No pending invoices to review.</h3>
    <p className={styles.emptySubtitle}>All billing cycles have been processed.</p>
  </div>
)

const QueueTable = ({ cycles, onSelectCycle }) => (
  <div className={styles.tableWrapper}>
    <table className={styles.queueTable}>
      <thead>
        <tr>
          <th>Client</th>
          <th>Campaign</th>
          <th>Period</th>
          <th>Usage</th>
          <th>Base</th>
          <th>Overage</th>
          <th>Total</th>
          <th>Invoices</th>
        </tr>
      </thead>
      <tbody>
        {cycles.map((cycle) => (
          <tr
            key={cycle.group_key}
            className={styles.queueRow}
            onClick={() => onSelectCycle(cycle)}
          >
            <td>{cycle.account_name || '\u2014'}</td>
            <td>{cycle.campaign_name || '\u2014'}</td>
            <td>{formatPeriod(cycle.period_start, cycle.period_end)}</td>
            <td className={styles.usageCell}>
              {cycle.actual_minutes != null
                ? `${parseFloat(cycle.actual_minutes).toFixed(1)} / `
                : ''}
              {cycle.included_minutes || 0} min
            </td>
            <td className={styles.currencyCell}>{formatCurrency(cycle.base_amount_minor)}</td>
            <td className={styles.currencyCell}>{formatCurrency(cycle.overage_amount_minor)}</td>
            <td className={styles.currencyCell}>{formatCurrency(cycle.draft_total_minor)}</td>
            <td>
              <span className={styles.invoiceBadge}>{cycle.draft_invoice_count || 0}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

QueueTable.propTypes = {
  cycles: PropTypes.array.isRequired,
  onSelectCycle: PropTypes.func.isRequired,
}

/* ── Review Form (single unified form) ──────────────────────────── */

const ReviewForm = ({
  cycleDetail,
  calculations,
  actualMinutes,
  setActualMinutes,
  baseFeeMinor,
  setBaseFeeMinor,
  overageRateMinor,
  setOverageRateMinor,
  taxEnabled,
  setTaxEnabled,
  taxRate,
  setTaxRate,
  contactEmail,
  setContactEmail,
  billingAddress,
  setBillingAddress,
}) => {
  const cycle = cycleDetail?.cycle
  const plan = cycleDetail?.plan
  const contacts = cycleDetail?.contacts || []
  const customer = cycleDetail?.customer
  const invoices = cycleDetail?.invoices || []
  const baseInvoice = invoices.find((inv) => inv.invoice_type === 'base') || invoices[0]
  const overageInvoice = invoices.find((inv) => inv.invoice_type === 'overage')
  const taxPercent = (plan?.tax_percent_basis_points || 0) / 100
  const hasOverage = (calculations?.overage || 0) > 0

  return (
    <>
      {/* Client & Plan Header */}
      <div className={styles.card}>
        <div className={styles.reviewHeaderInfo}>
          <div>
            <h3 className={styles.clientName}>{cycle?.account_name || '\u2014'}</h3>
            <p className={styles.campaignName}>{cycle?.campaign_name || '\u2014'}</p>
            <span className={styles.periodLabel}>
              {formatPeriod(cycle?.period_start, cycle?.period_end)}
            </span>
          </div>
          <div className={styles.planSummary}>
            <span className={styles.planBadge}>{plan?.plan_level_name || 'Standard'}</span>
            <span className={styles.currencyBadge}>
              {plan?.currency || customer?.billing_currency_default || 'CAD'}
            </span>
          </div>
        </div>
      </div>

      {/* Editable Form — all fields in a consistent grid */}
      <div className={styles.card}>
        <h4 className={styles.cardTitle}>
          <FontAwesomeIcon icon="edit" className={styles.cardTitleIcon} />
          Invoice Details
        </h4>

        <div className={styles.formGrid}>
          {/* Row 1: Contact & Address */}
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="contactEmail">
              Contact Email
            </label>
            <input
              id="contactEmail"
              type="email"
              className={styles.formInput}
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="billing@company.com"
            />
            {contacts.length > 0 && (
              <span className={styles.formHint}>
                On file:{' '}
                {contacts
                  .map((c) => c.email)
                  .filter(Boolean)
                  .join(', ')}
                {contacts.some((c) => c.is_primary) && ' (Primary)'}
              </span>
            )}
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="billingAddress">
              Billing Address
            </label>
            <input
              id="billingAddress"
              type="text"
              className={styles.formInput}
              value={billingAddress}
              onChange={(e) => setBillingAddress(e.target.value)}
              placeholder="123 Main St, City, Province"
            />
          </div>

          {/* Row 2: Plan reference */}
          <div className={styles.formField}>
            <span className={styles.formLabel}>Plan Base Fee</span>
            <span className={styles.formReadOnly}>
              {formatCurrency(plan?.fixed_monthly_fee_minor)}/mo
            </span>
          </div>
          <div className={styles.formField}>
            <span className={styles.formLabel}>Plan Overage Rate</span>
            <span className={styles.formReadOnly}>
              {formatCurrency(plan?.overage_per_minute_minor)}/min
            </span>
          </div>
          <div className={styles.formField}>
            <span className={styles.formLabel}>Tax Rate</span>
            <span className={styles.formReadOnly}>
              {taxPercent > 0 ? `${taxPercent}%` : 'None'}
            </span>
          </div>
          <div className={styles.formField}>
            <span className={styles.formLabel}>Due Days</span>
            <span className={styles.formReadOnly}>{plan?.due_days ?? 30}</span>
          </div>

          {/* Row 3: Usage & Pricing — all editable inputs */}
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="actualMinutes">
              Actual Minutes
            </label>
            <input
              id="actualMinutes"
              type="number"
              step="0.01"
              className={styles.formInput}
              value={actualMinutes}
              onChange={(e) => setActualMinutes(e.target.value)}
            />
            {parseFloat(actualMinutes) === 0 && (
              <span className={styles.formHint}>
                0 is normal for advance billing (current month just started)
              </span>
            )}
          </div>
          <div className={styles.formField}>
            <span className={styles.formLabel}>Included Minutes</span>
            <span className={styles.formReadOnly}>{calculations?.included ?? 0} min</span>
          </div>
          <div className={styles.formField}>
            <span className={styles.formLabel}>Overage Minutes</span>
            <span className={`${styles.formReadOnly} ${hasOverage ? styles.overageHighlight : ''}`}>
              {calculations?.overage?.toFixed(1) || '0.0'} min
            </span>
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="baseFee">
              Base Fee ($)
            </label>
            <input
              id="baseFee"
              type="number"
              step="0.01"
              className={styles.formInput}
              value={baseFeeMinor}
              onChange={(e) => setBaseFeeMinor(e.target.value)}
            />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="overageRate">
              Overage Rate ($/min)
            </label>
            <input
              id="overageRate"
              type="number"
              step="0.01"
              className={styles.formInput}
              value={overageRateMinor}
              onChange={(e) => setOverageRateMinor(e.target.value)}
            />
          </div>
          <div className={styles.formField}>
            <span className={styles.formLabel}>Charge Tax</span>
            <div className={styles.taxToggleRow}>
              <label className={styles.toggleLabel} htmlFor="taxEnabled">
                <input
                  id="taxEnabled"
                  type="checkbox"
                  checked={taxEnabled}
                  onChange={(e) => setTaxEnabled(e.target.checked)}
                />
                {taxEnabled ? 'Yes' : 'No'}
              </label>
              {taxEnabled && (
                <div className={styles.taxRateInput}>
                  <input
                    id="taxRate"
                    type="number"
                    step="0.01"
                    min="0"
                    className={styles.formInput}
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                  />
                  <span className={styles.taxRateUnit}>%</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Totals Preview */}
      <div className={styles.card}>
        <h4 className={styles.cardTitle}>
          <FontAwesomeIcon icon="file-invoice-dollar" className={styles.cardTitleIcon} />
          Invoice Totals
        </h4>
        <div className={styles.invoicePreview}>
          <div className={styles.invoiceCard}>
            <div className={styles.invoiceCardHeader}>
              <h5 className={styles.invoiceCardTitle}>Base Invoice</h5>
              {baseInvoice && (
                <span className={styles.invoiceNumber}>
                  {baseInvoice.invoice_number_local || `#${baseInvoice.id}`}
                </span>
              )}
            </div>
            <div className={styles.invoiceRow}>
              <span>Subtotal</span>
              <span>{formatCurrency(calculations?.baseFee)}</span>
            </div>
            <div className={styles.invoiceRow}>
              <span>Tax</span>
              <span>{taxEnabled ? formatCurrency(calculations?.baseTax) : '$0.00'}</span>
            </div>
            <div className={`${styles.invoiceRow} ${styles.invoiceTotal}`}>
              <span>Total</span>
              <span>{formatCurrency(calculations?.baseTotal)}</span>
            </div>
          </div>

          {hasOverage && (
            <div className={styles.invoiceCard}>
              <div className={styles.invoiceCardHeader}>
                <h5 className={styles.invoiceCardTitle}>Overage Invoice</h5>
                {overageInvoice && (
                  <span className={styles.invoiceNumber}>
                    {overageInvoice.invoice_number_local || `#${overageInvoice.id}`}
                  </span>
                )}
              </div>
              <div className={styles.invoiceRow}>
                <span>
                  Subtotal ({calculations?.overage?.toFixed(1)} min &times;{' '}
                  {formatCurrency(calculations?.overageRate)}/min)
                </span>
                <span>{formatCurrency(calculations?.overageAmount)}</span>
              </div>
              <div className={styles.invoiceRow}>
                <span>Tax</span>
                <span>{taxEnabled ? formatCurrency(calculations?.overageTax) : '$0.00'}</span>
              </div>
              <div className={`${styles.invoiceRow} ${styles.invoiceTotal}`}>
                <span>Total</span>
                <span>{formatCurrency(calculations?.overageTotal)}</span>
              </div>
            </div>
          )}
        </div>

        <div className={styles.grandTotal}>
          <span className={styles.grandTotalLabel}>Grand Total</span>
          <span className={styles.grandTotalValue}>{formatCurrency(calculations?.grandTotal)}</span>
        </div>
      </div>
    </>
  )
}

ReviewForm.propTypes = {
  cycleDetail: PropTypes.object,
  calculations: PropTypes.object,
  actualMinutes: PropTypes.string.isRequired,
  setActualMinutes: PropTypes.func.isRequired,
  baseFeeMinor: PropTypes.string.isRequired,
  setBaseFeeMinor: PropTypes.func.isRequired,
  overageRateMinor: PropTypes.string.isRequired,
  setOverageRateMinor: PropTypes.func.isRequired,
  taxEnabled: PropTypes.bool.isRequired,
  setTaxEnabled: PropTypes.func.isRequired,
  taxRate: PropTypes.string.isRequired,
  setTaxRate: PropTypes.func.isRequired,
  contactEmail: PropTypes.string.isRequired,
  setContactEmail: PropTypes.func.isRequired,
  billingAddress: PropTypes.string.isRequired,
  setBillingAddress: PropTypes.func.isRequired,
}

/**
 * Build the API URL for fetching a group's detail.
 */
function buildDetailUrl(group) {
  if (group.id) return apiEndpoints.billingPendingReviewItem(group.id)
  const ids = (group.invoice_ids || []).join(',')
  return `${apiEndpoints.billingPendingReviewByInvoices}?invoice_ids=${ids}`
}

const AdminBillingPendingReview = ({ embedded: _embedded }) => {
  const { cycles, loading, error, refetch } = usePendingReviewData()
  const { toast, showToast, closeToast } = useToast()

  const [selectedGroup, setSelectedGroup] = useState(null)
  const [cycleDetail, setCycleDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [approving, setApproving] = useState(false)
  const [cancelling, setCancelling] = useState(false)

  // Editable form state
  const [actualMinutes, setActualMinutes] = useState('')
  const [baseFeeMinor, setBaseFeeMinor] = useState('')
  const [overageRateMinor, setOverageRateMinor] = useState('')
  const [taxEnabled, setTaxEnabled] = useState(true)
  const [taxRate, setTaxRate] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [billingAddress, setBillingAddress] = useState('')

  useEffect(() => {
    if (!selectedGroup) return
    let cancelled = false
    setDetailLoading(true)
    authenticatedFetchJson(buildDetailUrl(selectedGroup))
      .then((res) => {
        if (cancelled) return
        const d = res?.data
        setCycleDetail(d || null)
        if (d) {
          setActualMinutes(String(parseFloat(d.cycle.actual_minutes) || 0))
          setBaseFeeMinor(String((d.plan?.fixed_monthly_fee_minor || 0) / 100))
          setOverageRateMinor(String((d.plan?.overage_per_minute_minor || 0) / 100))
          const planTaxBp = d.plan?.tax_percent_basis_points || 0
          setTaxEnabled(planTaxBp > 0)
          setTaxRate(String(planTaxBp / 100))
          const primary = (d.contacts || []).find((c) => c.is_primary)
          setContactEmail(primary?.email || d.customer?.billing_email_fallback || '')
          setBillingAddress(d.customer?.payor_address || '')
        }
      })
      .catch((err) => {
        if (cancelled) return
        logger.error('Failed to fetch cycle detail:', err)
        showToast(err.message || 'Failed to load review data', 'error')
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedGroup, showToast])

  const calculations = useMemo(() => {
    if (!cycleDetail) return null
    const actual = parseFloat(actualMinutes) || 0
    const included =
      parseInt(cycleDetail.cycle.included_minutes, 10) ||
      parseInt(cycleDetail.plan?.plan_minutes, 10) ||
      0
    const overage = Math.max(0, actual - included)
    const baseFee = Math.round((parseFloat(baseFeeMinor) || 0) * 100)
    const overageRate = Math.round((parseFloat(overageRateMinor) || 0) * 100)
    const overageAmount = Math.round(overage * overageRate)
    const taxBasis = taxEnabled ? Math.round((parseFloat(taxRate) || 0) * 100) : 0
    const baseTax = Math.round((baseFee * taxBasis) / 10000)
    const baseTotal = baseFee + baseTax
    const overageTax = overage > 0 ? Math.round((overageAmount * taxBasis) / 10000) : 0
    const overageTotal = overageAmount + overageTax
    const grandTotal = baseTotal + (overage > 0 ? overageTotal : 0)
    return {
      actual,
      included,
      overage,
      baseFee,
      overageRate,
      overageAmount,
      taxBasis,
      baseTax,
      baseTotal,
      overageTax,
      overageTotal,
      grandTotal,
    }
  }, [actualMinutes, baseFeeMinor, overageRateMinor, taxEnabled, taxRate, cycleDetail])

  const currentIndex = useMemo(() => {
    if (!selectedGroup || !cycles.length) return -1
    return cycles.findIndex((c) => c.group_key === selectedGroup.group_key)
  }, [selectedGroup, cycles])

  const handleApprove = useCallback(async () => {
    if (!cycleDetail || !selectedGroup) return
    const parsedMinutes = parseFloat(actualMinutes)
    const parsedBase = parseFloat(baseFeeMinor)
    const parsedOverage = parseFloat(overageRateMinor)
    if (isNaN(parsedBase) || parsedBase < 0) {
      showToast('Base fee must be a valid non-negative number', 'error')
      return
    }
    if (isNaN(parsedOverage) || parsedOverage < 0) {
      showToast('Overage rate must be a valid non-negative number', 'error')
      return
    }
    setApproving(true)
    try {
      const overrides = {
        actual_minutes: isNaN(parsedMinutes) ? 0 : parsedMinutes,
        base_fee_minor: Math.round(parsedBase * 100),
        overage_rate_minor: Math.round(parsedOverage * 100),
        tax_enabled: taxEnabled,
        tax_rate_basis_points: taxEnabled ? Math.round((parseFloat(taxRate) || 0) * 100) : 0,
        contact_email: contactEmail || undefined,
        billing_address: billingAddress || undefined,
      }
      const approveId = selectedGroup.id || 0
      const res = await authenticatedFetchJson(apiEndpoints.billingPendingReviewApprove(approveId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrides, invoice_ids: selectedGroup.invoice_ids }),
      })
      showToast(`Approved ${res?.data?.invoices_approved || 0} invoices`, 'success')
      const nextIdx = currentIndex + 1
      const nextGroup = nextIdx < cycles.length ? cycles[nextIdx] : null
      refetch()
      if (nextGroup) {
        setSelectedGroup(nextGroup)
      } else {
        setSelectedGroup(null)
        setCycleDetail(null)
      }
    } catch (err) {
      showToast(err.message || 'Approval failed', 'error')
    } finally {
      setApproving(false)
    }
  }, [
    cycleDetail,
    selectedGroup,
    actualMinutes,
    baseFeeMinor,
    overageRateMinor,
    taxEnabled,
    taxRate,
    contactEmail,
    billingAddress,
    showToast,
    refetch,
    currentIndex,
    cycles,
  ])

  const handleCancel = useCallback(async () => {
    if (!selectedGroup) return
    if (
      !window.confirm(
        'Cancel all invoices for this billing cycle? This will void them on Stripe and cannot be undone.',
      )
    )
      return
    setCancelling(true)
    try {
      const ids = selectedGroup.invoice_ids || []
      for (const invoiceId of ids) {
        await authenticatedFetchJson(apiEndpoints.billingInvoiceCancel(invoiceId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: 'Cancelled during invoice review' }),
        })
      }
      showToast(`Cancelled ${ids.length} invoice${ids.length !== 1 ? 's' : ''}`, 'success')
      const nextIdx = currentIndex + 1
      const nextGroup = nextIdx < cycles.length ? cycles[nextIdx] : null
      refetch()
      if (nextGroup) {
        setSelectedGroup(nextGroup)
      } else {
        setSelectedGroup(null)
        setCycleDetail(null)
      }
    } catch (err) {
      showToast(err.message || 'Failed to cancel invoices', 'error')
    } finally {
      setCancelling(false)
    }
  }, [selectedGroup, showToast, refetch, currentIndex, cycles])

  const handleSkip = useCallback(() => {
    const nextIdx = currentIndex + 1
    if (nextIdx < cycles.length) {
      setSelectedGroup(cycles[nextIdx])
    } else {
      setSelectedGroup(null)
      setCycleDetail(null)
    }
  }, [currentIndex, cycles])

  const handleBackToQueue = useCallback(() => {
    setSelectedGroup(null)
    setCycleDetail(null)
  }, [])

  const handleStartReview = useCallback(() => {
    if (cycles.length > 0) setSelectedGroup(cycles[0])
  }, [cycles])

  const handleSelectCycle = useCallback((group) => {
    setSelectedGroup(group)
  }, [])

  // Queue View
  if (!selectedGroup) {
    return (
      <div className={styles.container}>
        <OpsBar showToast={showToast} onComplete={refetch} />
        {loading && (
          <div className={styles.loadingWrapper}>
            <LoadingSpinner message="Loading pending invoices..." />
          </div>
        )}
        {!loading && error && (
          <div className={styles.errorState}>
            <FontAwesomeIcon icon="exclamation-triangle" /> {error}
          </div>
        )}
        {!loading && !error && cycles.length === 0 && <EmptyQueue />}
        {!loading && !error && cycles.length > 0 && (
          <div className={styles.queueSection}>
            <div className={styles.queueActions}>
              <span className={styles.queueCount}>{cycles.length} pending review</span>
              <button type="button" className={styles.startButton} onClick={handleStartReview}>
                <FontAwesomeIcon icon="arrow-right" /> Start Review
              </button>
            </div>
            <QueueTable cycles={cycles} onSelectCycle={handleSelectCycle} />
          </div>
        )}
        <ToastNotification
          open={toast.open}
          message={toast.message}
          severity={toast.severity}
          onClose={closeToast}
        />
      </div>
    )
  }

  // Review View
  return (
    <div className={styles.container}>
      {detailLoading && (
        <div className={styles.detailLoading}>
          <LoadingSpinner message="Loading invoice details..." />
        </div>
      )}

      {!detailLoading && cycleDetail && (
        <>
          <div className={styles.reviewHeader}>
            <button type="button" className={styles.secondaryButton} onClick={handleBackToQueue}>
              <FontAwesomeIcon icon="arrow-left" /> Back
            </button>
            <span className={styles.reviewPosition}>
              Reviewing {currentIndex >= 0 ? currentIndex + 1 : '?'} of {cycles.length}
            </span>
            <button type="button" className={styles.skipButton} onClick={handleSkip}>
              Skip <FontAwesomeIcon icon="arrow-right" />
            </button>
          </div>

          <ReviewForm
            cycleDetail={cycleDetail}
            calculations={calculations}
            actualMinutes={actualMinutes}
            setActualMinutes={setActualMinutes}
            baseFeeMinor={baseFeeMinor}
            setBaseFeeMinor={setBaseFeeMinor}
            overageRateMinor={overageRateMinor}
            setOverageRateMinor={setOverageRateMinor}
            taxEnabled={taxEnabled}
            setTaxEnabled={setTaxEnabled}
            taxRate={taxRate}
            setTaxRate={setTaxRate}
            contactEmail={contactEmail}
            setContactEmail={setContactEmail}
            billingAddress={billingAddress}
            setBillingAddress={setBillingAddress}
          />

          <div className={styles.actionBar}>
            <div className={styles.actionBarLeft}>
              <button type="button" className={styles.secondaryButton} onClick={handleBackToQueue}>
                <FontAwesomeIcon icon="arrow-left" /> Back
              </button>
            </div>
            <span className={styles.actionBarCenter}>
              {currentIndex >= 0 ? currentIndex + 1 : '?'} of {cycles.length}
            </span>
            <div className={styles.actionBarRight}>
              <button
                type="button"
                className={styles.dangerButton}
                disabled={cancelling || approving}
                onClick={handleCancel}
              >
                {cancelling ? 'Cancelling\u2026' : 'Cancel Invoice'}
              </button>
              <button type="button" className={styles.skipButton} onClick={handleSkip}>
                Skip <FontAwesomeIcon icon="arrow-right" />
              </button>
              <button
                type="button"
                className={styles.primaryButton}
                disabled={approving || cancelling}
                onClick={handleApprove}
              >
                {approving ? 'Approving\u2026' : 'Approve & Send'}
              </button>
            </div>
          </div>
        </>
      )}

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={closeToast}
      />
    </div>
  )
}

AdminBillingPendingReview.propTypes = {
  embedded: PropTypes.bool,
}

export default AdminBillingPendingReview
